import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';

function buildParams(obj) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, v);
  }
  const str = params.toString();
  return str ? `?${str}` : '';
}

/**
 * Liste paginée des produits.
 * Filtres : q (SKU/ref/nom), status, page, per_page.
 */
export function useProducts({ q, status, page = 1, per_page = 50 } = {}) {
  return useQuery({
    queryKey: ['products', 'list', { q, status, page, per_page }],
    queryFn: () => api.get(`/products${buildParams({ q, status, page, per_page })}`),
    staleTime: 60_000,
  });
}

/**
 * Détail d'un produit par SKU avec historique des ventes.
 */
export function useProduct(sku) {
  return useQuery({
    queryKey: ['products', 'detail', sku],
    queryFn: () => api.get(`/products/${encodeURIComponent(sku)}`),
    staleTime: 60_000,
    enabled: Boolean(sku),
  });
}

/**
 * Mutation upload xlsx inventaire.
 * Invalide le cache ['products'] au succès.
 * Retourne { inserted, updated, skipped, errors_count, duration_ms }.
 */
export function useImportProducts() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const BASE = import.meta.env.VITE_API_URL ?? '/v1';
      const res = await fetch(`${BASE}/products/import`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (res.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        throw new Error('Session expirée');
      }

      let json;
      try {
        json = await res.json();
      } catch {
        if (res.status === 413) {
          throw new Error('Fichier trop volumineux (limite serveur dépassée)');
        }
        throw new Error(`Réponse non-JSON du serveur (HTTP ${res.status})`);
      }

      if (!res.ok) {
        const errObj = json?.error ?? json;
        throw new Error(errObj?.message ?? json?.message ?? `Erreur serveur (HTTP ${res.status})`);
      }

      return json;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
